import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useWindowDimensions } from "resources/BrowserManager";
import ApiManager from "resources/ApiManager/api";

import {
  theme,
  Flex,
  Space,
  Layout,
  Typography,
  Button,
  Form,
  Input,
  Spin,
} from "antd";
const { Content } = Layout;
const { Title, Text } = Typography;
import {
  MailOutlined,
  CheckCircleOutlined,
  ArrowLeftOutlined,
  LockOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import CONSTANTS from "shared/constants";
import strings from "resources/strings";
import FooterContent from "components/Shared/FooterContent";
import FloatingCard from "../../../components/Shared/FloatingCard";

import "./style.scss";

const styleWrapperClass = "reset";

export default function PasswordReset() {
  const { isSmallView } = useWindowDimensions();

  const { token: theme_token } = theme.useToken();

  const navigate = useNavigate();

  const [form] = Form.useForm();
  const [isSubmitting, setSubmitting] = useState(false);
  const [modalErrorTitle, setModalErrorTitle] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [emailValue, setEmailValue] = useState(null);

  const [isOTPReady, setIsOTPReady] = useState(false);
  const [isOTPSent, setIsOTPSent] = useState(false);
  const [isHandlingOTPSent, setIsHandlingOTPSent] = useState(false);
  const [OTPValue, setOTPValue] = useState(null);

  const inputOTPRef = useRef(null);

  const isInvalidEmail =
    !!!emailValue || emailValue.indexOf("@") < 0 || emailValue.indexOf(".") < 0;

  useEffect(() => {
    if (inputOTPRef) {
      inputOTPRef?.current?.focus();
    }
  }, [isOTPSent]);

  const updateEmailValue = (e) => {
    setEmailValue(e.target.value);
    setModalErrorTitle(null);
  };

  const handleOTPSent = async () => {
    setModalErrorTitle(null);
    setIsHandlingOTPSent(true);
    const apiRes = await ApiManager.getUserEmailToken(
      "password_reset",
      emailValue
    );
    setIsHandlingOTPSent(false);

    //Silently return when error was for session expired, as the api handler will handle the message and redirection to /login
    if (apiRes.response.code === CONSTANTS.API_RESPONSE_ERROR_SESSION_EXPIRED) {
      return;
    }

    if (apiRes.response.code === 3002) {
      setModalErrorTitle(strings.registerEmailAlreadyExists);
      return;
    } else if (apiRes.response.code !== 2000) {
      setModalErrorTitle(strings.alertUnexpectedError);
      return;
    }
    setIsOTPSent(true);
  };

  const onOTPInput = (value) => {
    if (!value.target.value) {
      setIsOTPReady(false);
      setModalErrorTitle(null);
    }
  };

  const onOTPChange = (text) => {
    setIsOTPReady(true);
    setOTPValue(text);
    setModalErrorTitle(null);
  };

  const onFinish = async (values) => {
    setSubmitting(true);
    setModalErrorTitle(null);

    const apiRes = await ApiManager.postUserPasswordReset(
      OTPValue,
      values.email,
      values.password
    );

    setSubmitting(false);

    //Silently return when error was for session expired, as the api handler will handle the message and redirection to /login
    if (apiRes.response.code === CONSTANTS.API_RESPONSE_ERROR_SESSION_EXPIRED) {
      return;
    }

    if (apiRes.response.code === 3000) {
      setModalErrorTitle(strings.registerInvalidVerificationToken);
      return;
    } else if (apiRes.response.code !== 2000) {
      setModalErrorTitle(strings.alertUnexpectedError);
      return;
    }

    form.resetFields();

    setIsSubmitted(true);
  };

  const resetForm = (
    <Space direction="vertical" size={15}>
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
        Cancelar y regresar
      </Button>
      <Form form={form} onFinish={onFinish} style={{ maxWidth: "500px" }}>
        <FloatingCard title="Recupera tu contraseña">
          <Spin spinning={false}>
            <Flex vertical gap={10}>
              <Space direction="vertical" size={0}>
                <Text>
                  Ingresa tu correo electrónico, se enviará un código de
                  verificación como método de identificación.
                </Text>
              </Space>

              <Form.Item
                name="email"
                rules={[
                  {
                    required: true,
                    message: strings.validationEmailRequired,
                  },
                ]}
                style={{ marginBottom: "5px" }}
              >
                <Space.Compact style={{ width: "100%" }}>
                  <Input
                    prefix={
                      <MailOutlined
                        style={{ color: theme_token.colorPrimary }}
                      />
                    }
                    type="email"
                    placeholder={strings.formEmail}
                    onChange={updateEmailValue}
                    disabled={!!isOTPSent || !!isHandlingOTPSent}
                  />
                </Space.Compact>
              </Form.Item>

              <Flex vertical gap={10} style={{ marginBottom: "10px" }}>
                <Flex gap={20} vertical={isSmallView}>
                  <Button
                    type="primary"
                    disabled={isOTPSent || isInvalidEmail}
                    loading={isHandlingOTPSent}
                    onClick={handleOTPSent}
                    icon={isOTPSent ? <CheckOutlined /> : null}
                  >
                    {isOTPSent ? "Código enviado" : "Verificar Correo"}
                  </Button>
                  <Flex justify="center">
                    <Input.OTP
                      formatter={(str) => str.toUpperCase()}
                      onInput={onOTPInput}
                      onChange={onOTPChange}
                      disabled={!!!isOTPSent}
                      ref={inputOTPRef}
                      style={{
                        width: "210px",
                        textAlign: "center",
                      }}
                    />
                  </Flex>
                </Flex>
                {isOTPSent && (
                  <Text>
                    Revisa tu correo electrónico e introduce el código recibido.
                  </Text>
                )}
              </Flex>

              {isOTPReady && (
                <>
                  <Form.Item
                    name="password"
                    rules={[
                      {
                        required: true,
                        message: strings.validationPasswordRequired,
                      },
                      () => ({
                        validator(_, value) {
                          if (!value || value.length >= 8) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error(
                              "¡Tu contraseña debe tener al menos 8 caractéres!"
                            )
                          );
                        },
                      }),
                    ]}
                    style={{ marginBottom: "0px" }}
                  >
                    <Input.Password
                      prefix={
                        <LockOutlined
                          style={{ color: theme_token.colorPrimary }}
                        />
                      }
                      placeholder={strings.formPassword}
                    />
                  </Form.Item>

                  <Form.Item
                    name="confirm"
                    dependencies={["password"]}
                    rules={[
                      {
                        required: true,
                        message: strings.validationPasswordRequired,
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue("password") === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(
                            new Error("¡Las contraseñas no coinciden!")
                          );
                        },
                      }),
                    ]}
                    style={{ marginBottom: "10px" }}
                  >
                    <Input.Password
                      prefix={
                        <LockOutlined
                          style={{ color: theme_token.colorPrimary }}
                        />
                      }
                      placeholder="Confirmar Contraseña"
                    />
                  </Form.Item>
                </>
              )}

              {modalErrorTitle && (
                <Text
                  style={{ color: theme_token.colorError, fontWeight: "600" }}
                >
                  {modalErrorTitle}
                </Text>
              )}

              {isOTPReady && (
                <Button
                  size="medium"
                  type="primary"
                  htmlType="submit"
                  loading={isSubmitting}
                  block
                >
                  Cambiar contraseña
                </Button>
              )}
            </Flex>
          </Spin>
        </FloatingCard>
      </Form>
    </Space>
  );

  const submittedView = (
    <Space direction="vertical" size={15} style={{ marginTop: "100px" }}>
      <FloatingCard>
        <Flex align="center" vertical gap={5}>
          <CheckCircleOutlined
            style={{ fontSize: "40px", color: theme_token.colorPrimary }}
          />
          <Title level={3} style={{ textAlign: "center" }}>
            ¡Tu contraseña ha sido actualizada!
          </Title>
          <Title level={5} style={{ textAlign: "center" }}>
            Podrás iniciar sesión con tu nueva contraseña.
          </Title>
          <Button type="primary" onClick={() => navigate("/login")}>
            Iniciar Sesión
          </Button>
        </Flex>
      </FloatingCard>
    </Space>
  );

  return (
    <div className={styleWrapperClass}>
      <Layout>
        <Content style={{ marginTop: "50px" }}>
          <Flex align="center" vertical gap={30}>
            <img
              src={strings.url_logo_colored_svg}
              alt={strings.appName}
              style={{
                width: "200px",
              }}
            />
            {isSubmitted ? submittedView : resetForm}
          </Flex>
        </Content>
        <FooterContent />
      </Layout>
    </div>
  );
}
